import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, CaretUp, CaretDown, Minus } from "@phosphor-icons/react";
import axios from "axios";
import { API } from "@/App";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";
import { playerToSlug } from "@/lib/playerSlug";

const PC = { QB: "#ff6b6b", RB: "#4ecdc4", WR: "#45b7d1", TE: "#f9ca24" };
const POS_LABELS = { qb: "Quarterback", rb: "Running Back", wr: "Wide Receiver", te: "Tight End" };

function TrendBadge({ value }) {
  if (!value || value === 0) return <span className="text-[#666] text-xs font-mono"><Minus size={10} className="inline" /> 0</span>;
  if (value > 0) return <span className="text-[#4ade80] text-xs font-mono"><CaretUp size={10} weight="fill" className="inline" /> +{value}</span>;
  return <span className="text-[#ff3b30] text-xs font-mono"><CaretDown size={10} weight="fill" className="inline" /> {value}</span>;
}

export default function PlayersIndex() {
  // Detect position from URL path: /players/qb, /players/rb, etc.
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const posMatch = pathname.match(/^\/players\/(qb|rb|wr|te)$/i);
  const posFilter = posMatch ? posMatch[1].toUpperCase() : "ALL";
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/fantasycalc/values?numQbs=2&numTeams=12&ppr=1`);
        setPlayers(res.data.filter(p => p.player?.position && ["QB","RB","WR","TE"].includes(p.player.position) && !p.player.name?.includes("Pick")));
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const filtered = players.filter(p => {
    if (posFilter !== "ALL" && p.player.position !== posFilter) return false;
    if (search && !p.player.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const pageTitle = posFilter === "ALL"
    ? "Dynasty Player Directory: 400+ Player Pages with Values, Trades & Analysis — Dynasty Trade Maker"
    : `Dynasty ${POS_LABELS[posFilter.toLowerCase()] || posFilter} Rankings & Values — Dynasty Trade Maker`;
  const pageDesc = posFilter === "ALL"
    ? "Browse every dynasty fantasy football player. Live values, trade data, championship rates, depth charts, and AI dynasty outlook for 400+ players."
    : `All dynasty ${POS_LABELS[posFilter.toLowerCase()] || posFilter}s ranked by Superflex value. Live FantasyCalc values, 30-day trends, and links to full player profiles.`;
  const pagePath = posFilter === "ALL" ? "/players" : `/players/${posFilter.toLowerCase()}`;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title={pageTitle} description={pageDesc} path={pagePath} />
      <PublicNav />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-2">
          {posFilter === "ALL" ? <>PLAYER <span className="text-[#ccff00]">DIRECTORY</span></> : <><span className="text-[#ccff00]">{posFilter}</span> RANKINGS</>}
        </h1>
        <p className="text-sm text-[#a3a3a3] mb-6">
          {posFilter === "ALL" ? "Every dynasty player with live values, trends, and real trade data." : `All dynasty ${POS_LABELS[posFilter.toLowerCase()] || posFilter}s ranked by Superflex value with 30-day trends.`}
          {" "}<span className="text-[#555]">{filtered.length} players</span>
        </p>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search players..." className="w-full h-9 pl-9 pr-3 bg-[#171717] border border-[#262626] text-white text-sm rounded-sm placeholder:text-[#555] focus:border-[#ccff00] focus:outline-none" data-testid="player-search" />
          </div>
          <div className="flex gap-1">
            {[
              { label: "ALL", href: "/players" },
              { label: "QB", href: "/players/qb" },
              { label: "RB", href: "/players/rb" },
              { label: "WR", href: "/players/wr" },
              { label: "TE", href: "/players/te" },
            ].map(btn => (
              <Link key={btn.label} to={btn.href} className={`px-3 py-1.5 text-[10px] font-bold uppercase rounded-sm transition-colors no-underline ${posFilter === btn.label ? "bg-[#ccff00] text-black" : "border border-[#262626] text-[#888] hover:border-[#444]"}`} data-testid={`filter-${btn.label.toLowerCase()}`}>{btn.label}</Link>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="space-y-2">{[...Array(10)].map((_,i) => <div key={i} className="h-10 skeleton rounded-sm" />)}</div>
        ) : (
          <div className="border border-[#262626] overflow-hidden">
            <div className="hidden sm:grid grid-cols-8 bg-[#0a0a0a] px-3 py-2 border-b border-[#262626] text-[9px] font-bold uppercase tracking-wider text-[#555]">
              <span>#</span><span className="col-span-3">Player</span><span>Pos</span><span>Team</span><span className="text-right">Value</span><span className="text-right">Trend</span>
            </div>
            <div className="sm:hidden grid grid-cols-6 bg-[#0a0a0a] px-3 py-2 border-b border-[#262626] text-[9px] font-bold uppercase tracking-wider text-[#555]">
              <span>#</span><span className="col-span-3">Player</span><span className="text-right">Value</span><span className="text-right">Trend</span>
            </div>
            {filtered.map((pv, i) => {
              const p = pv.player;
              const slug = playerToSlug(p.name);
              const img = p.sleeperId ? `https://sleepercdn.com/content/nfl/players/thumb/${p.sleeperId}.jpg` : null;
              const rank = posFilter === "ALL" ? (pv.overallRank || i+1) : (pv.positionRank || i+1);
              return (
                <Link key={i} to={`/players/${slug}`} className="items-center px-3 py-2 border-b border-[#1a1a1a] no-underline hover:bg-[#1a1a1a] transition-colors hidden sm:grid grid-cols-8" data-testid={`player-row-${i}`}>
                  <span className="text-xs text-[#555]">{rank}</span>
                  <span className="col-span-3 flex items-center gap-2 min-w-0">
                    {img && <img src={img} alt="" className="w-6 h-6 rounded-sm object-cover shrink-0" onError={e => e.target.style.display='none'} />}
                    <span className="text-xs text-white truncate">{p.name}</span>
                  </span>
                  <span className="text-xs" style={{color: PC[p.position]}}>{p.position}</span>
                  <span className="text-xs text-[#888]">{p.maybeTeam || "FA"}</span>
                  <span className="text-xs font-mono text-[#ccff00] text-right">{pv.value?.toLocaleString()}</span>
                  <span className="text-right"><TrendBadge value={pv.trend30Day} /></span>
                </Link>
              );
            })}
            {/* Mobile rows */}
            {filtered.map((pv, i) => {
              const p = pv.player;
              const slug = playerToSlug(p.name);
              const img = p.sleeperId ? `https://sleepercdn.com/content/nfl/players/thumb/${p.sleeperId}.jpg` : null;
              const rank = posFilter === "ALL" ? (pv.overallRank || i+1) : (pv.positionRank || i+1);
              return (
                <Link key={`m-${i}`} to={`/players/${slug}`} className="grid grid-cols-6 items-center px-3 py-2 border-b border-[#1a1a1a] no-underline hover:bg-[#1a1a1a] transition-colors sm:hidden" data-testid={`player-row-m-${i}`}>
                  <span className="text-xs text-[#555]">{rank}</span>
                  <span className="col-span-3 flex items-center gap-2 min-w-0">
                    {img && <img src={img} alt="" className="w-5 h-5 rounded-sm object-cover shrink-0" onError={e => e.target.style.display='none'} />}
                    <span className="min-w-0">
                      <span className="text-xs text-white block truncate">{p.name}</span>
                      <span className="text-[9px] text-[#666]">{p.position} · {p.maybeTeam || "FA"}</span>
                    </span>
                  </span>
                  <span className="text-xs font-mono text-[#ccff00] text-right">{pv.value?.toLocaleString()}</span>
                  <span className="text-right"><TrendBadge value={pv.trend30Day} /></span>
                </Link>
              );
            })}
          </div>
        )}

        <div className="mt-8 text-center text-[10px] text-[#444] space-x-3">
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
          <Link to="/compare" className="hover:text-[#a3a3a3] no-underline">Compare</Link>
          <Link to="/trades" className="hover:text-[#a3a3a3] no-underline">Trades</Link>
        </div>
      </div>
    </div>
  );
}
